import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

const StyledAppBar = styled(AppBar)({
  backgroundColor: '#000',
  boxShadow: 'none',
});

const Logo = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  color: '#fff',
  textDecoration: 'none',
  fontSize: '2rem',
  flexGrow: 1,
  textAlign: 'center',
});

const IconContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const StyledIconButton = styled(IconButton)(({ edge }) => ({
  color: '#fff',
  padding: edge === 'start' ? '12px 0 12px 8px' : '12px 8px 12px 0',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#c4a661',
  },
}));

const DrawerContent = styled(Box)({
  width: 250,
  padding: '1rem',
  backgroundColor: '#000',
  height: '100%',
  color: '#fff',
});

const StyledListItem = styled(ListItem)({
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '& .MuiListItemText-primary': {
    color: '#fff',
  },
});

const menuItems = [
  { text: 'Jewelry', link: '/jewelry' },
  { text: 'High Jewelry', link: '/high-jewelry' },
  { text: 'Collections', link: '/collections' },
  { text: 'Watches', link: '/watches' },
  { text: 'Services', link: '/services' },
  { text: 'Design Studio', link: '/design' },
  { text: 'Custom Inquiries', link: '/custom-inquiries' },
  { text: 'Our Story', link: '/our-story' },
  { text: 'Affiliate Program', link: '/affiliate' },
];

const jewelryItems = [
  { name: 'Rings', path: '/jewelry/rings' },
  { name: 'Necklaces', path: '/jewelry/necklaces' },
  { name: 'Earrings', path: '/jewelry/earrings' },
  { name: 'Piercings', path: '/jewelry/piercings' },
  { name: 'Bracelets', path: '/jewelry/bracelets' },
];

const collectionItems = [
  { name: "Women's Collection", path: '/collections?gender=women' },
  { name: "Men's Collection", path: '/collections?gender=men' },
  { name: 'Unisex Collection', path: '/collections?gender=unisex' },
];

const Navbar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [jewelryOpen, setJewelryOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);

  const handleJewelryClick = () => {
    setJewelryOpen(!jewelryOpen);
  };

  const handleCollectionsClick = () => {
    setCollectionsOpen(!collectionsOpen);
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <StyledAppBar position="sticky">
      <Container maxWidth={false} disableGutters>
        <Toolbar sx={{ px: 0 }}>
          <StyledIconButton
            edge="start"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </StyledIconButton>

          <Logo
            variant="h6"
            component={Link}
            to="/"
          >
            Custom Connect Jewelry
          </Logo>

          <IconContainer>
            <StyledIconButton
              aria-label="cart"
              component={Link}
              to="/checkout"
            >
              <Badge badgeContent={cartItemCount} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </StyledIconButton>

            <StyledIconButton
              aria-label="account"
              component={Link}
              to={user ? '/profile' : '/login'}
            >
              <PersonIcon />
            </StyledIconButton>
          </IconContainer>
        </Toolbar>
      </Container>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#000',
          },
        }}
      >
        <DrawerContent>
          <List>
            <StyledListItem button onClick={handleJewelryClick}>
              <ListItemText primary="Jewelry" />
              {jewelryOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
            </StyledListItem>
            <Collapse in={jewelryOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {jewelryItems.map((item) => (
                  <StyledListItem
                    key={item.path}
                    button
                    sx={{ pl: 4 }}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(item.path);
                    }}
                  >
                    <ListItemText primary={item.name} />
                  </StyledListItem>
                ))}
              </List>
            </Collapse>
            <StyledListItem button onClick={handleCollectionsClick}>
              <ListItemText primary="Collections" />
              {collectionsOpen ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
            </StyledListItem>
            <Collapse in={collectionsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {collectionItems.map((item) => (
                  <StyledListItem
                    key={item.path}
                    button
                    sx={{ pl: 4 }}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      navigate(item.path);
                    }}
                  >
                    <ListItemText primary={item.name} />
                  </StyledListItem>
                ))}
              </List>
            </Collapse>
            {menuItems.filter(item => !['Jewelry', 'Collections'].includes(item.text)).map((item) => (
              <StyledListItem
                key={item.link}
                button
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate(item.link);
                }}
              >
                <ListItemText primary={item.text} />
              </StyledListItem>
            ))}
            <Divider sx={{ my: 2, backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
            <StyledListItem
              button
              onClick={() => {
                setMobileMenuOpen(false);
                navigate(user ? '/profile' : '/login');
              }}
            >
              <ListItemText primary={user ? 'My Account' : 'Sign In'} />
            </StyledListItem>
          </List>
        </DrawerContent>
      </Drawer>
    </StyledAppBar>
  );
};

export default Navbar;
